/**
 * MetaAppReviewHub — iter-254 (Feb 2026)
 * ======================================
 * The single centralised UI to plan, fire and monitor every Meta App
 * Review test API call across the 11 capabilities the app has requested
 * access to. Mirrors the Meta Developer Dashboard 1-to-1 but adds
 * one-click "Run All Pending" per capability and per permission.
 *
 * Path: لوحة التحكم → التسويق → CLOUDGM FZ LLC (تسويق) → Meta App Review Testing
 * Route: /marketing/meta-app-review
 *
 * Uses the same WhatsApp Cloud API access token (System-User token) that
 * powers the WhatsApp integration — no new credentials needed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, RefreshCw, CheckCircle2, AlertCircle, XCircle, ChevronDown,
  ChevronUp, Play, RotateCcw, Zap, ArrowRight, ArrowLeft, ExternalLink,
  Shield, Wifi, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Meta-style icon per capability id
const CAP_ICON_COLOR = {
  ig_messaging_content:  { bg: 'bg-pink-500/10',   border: 'border-pink-500/30',   text: 'text-pink-400',   emoji: '📸' },
  catalog_api:           { bg: 'bg-cyan-500/10',   border: 'border-cyan-500/30',   text: 'text-cyan-400',   emoji: '🛒' },
  threads_api:           { bg: 'bg-white/5',       border: 'border-white/20',      text: 'text-white/80',   emoji: '@' },
  marketing_api_create:  { bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  text: 'text-amber-400',  emoji: '📣' },
  marketing_api_measure: { bg: 'bg-emerald-500/10',border: 'border-emerald-500/30',text: 'text-emerald-400',emoji: '📊' },
  marketing_api_leads:   { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', emoji: '🎯' },
  page_full_control:     { bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   text: 'text-blue-400',   emoji: '📘' },
  messenger_engage:      { bg: 'bg-sky-500/10',    border: 'border-sky-500/30',    text: 'text-sky-400',    emoji: '💬' },
  ads_mcp_server:        { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', emoji: '🖥️' },
  audience_network:      { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', emoji: '$' },
  whatsapp:              { bg: 'bg-green-500/10',  border: 'border-green-500/30',  text: 'text-green-400',  emoji: '✅' },
};

const STATUS_STYLE = {
  not_started: { pill: 'bg-white/5 text-white/50 border-white/10',       label_ar: 'لم يبدأ',    label_en: 'Not started' },
  in_progress: { pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label_ar: 'قيد الاختبار', label_en: 'Testing in progress' },
  completed:   { pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label_ar: 'اكتمل', label_en: 'Completed' },
};

export default function MetaAppReviewHub() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenPresent, setTokenPresent] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});   // { [capId]: true }
  const [whoami, setWhoami] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/meta-review/capabilities`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/meta-review/whoami`, { headers: getAuthHeaders() }),
      ]);
      setCaps(r1.data?.data || []);
      setTokenPresent(!!r1.data?.token_present);
      setWhoami(r2.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const runAll = async (capId) => {
    setBusy((b) => ({ ...b, [capId]: true }));
    try {
      const r = await axios.post(`${API}/admin/meta-review/capabilities/${capId}/run-all?cap_limit=25`,
                                  {}, { headers: getAuthHeaders(), timeout: 55_000 });
      const ok = r.data?.ok_count || 0;
      const fail = r.data?.failed_count || 0;
      const stopped = r.data?.stopped_early;
      const skips = (r.data?.aggregate || []).filter((x) => x.skipped && (x.reason || '').toLowerCase().includes('app-level'));
      const hitAppLimit = skips.length > 0;
      if (hitAppLimit) {
        toast.error(t(
          `تم الوصول لحد Meta على مستوى التطبيق (ساعة كاملة) — أرسل ${ok} استدعاء ثم توقف. حاول بعد ساعة.`,
          `Meta app-level rate limit hit (hourly) — fired ${ok} then stopped. Retry after ~1 hour.`,
        ));
      } else {
        toast.success(t(
          `تم إطلاق ${ok + fail} استدعاء — ${ok} نجاح · ${fail} فشل${stopped ? ' (توقف مبكر — اضغط "Run All" مرة أخرى للمتابعة)' : ''}`,
          `Fired ${ok + fail} calls — ${ok} ok · ${fail} failed${stopped ? ' (stopped early — click "Run All" again to keep firing)' : ''}`,
        ));
      }
      setCaps((prev) => prev.map((c) => c.id === capId ? r.data.capability : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy((b) => ({ ...b, [capId]: false }));
    }
  };

  const runOne = async (capId, permName, times = 1) => {
    setBusy((b) => ({ ...b, [`${capId}:${permName}`]: true }));
    try {
      const r = await axios.post(
        `${API}/admin/meta-review/capabilities/${capId}/run-test`,
        { permission: permName, times },
        { headers: getAuthHeaders() },
      );
      const ok = r.data?.ok_count || 0;
      const fail = r.data?.failed_count || 0;
      if (fail > 0) {
        toast.error(t(
          `${permName}: ${ok} نجاح · ${fail} فشل — راجع سجل الاستدعاءات`,
          `${permName}: ${ok} ok · ${fail} failed — check the call log`,
        ));
      } else {
        toast.success(t(`${permName}: تم ${ok} استدعاء ناجح`, `${permName}: ${ok} calls ok`));
      }
      setCaps((prev) => prev.map((c) => c.id === capId ? r.data.capability : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy((b) => ({ ...b, [`${capId}:${permName}`]: false }));
    }
  };

  const resetCap = async (capId) => {
    if (!window.confirm(t(
      'إعادة تصفير جميع عدادات هذه القدرة؟ (سيتم مسح سجل الاستدعاءات أيضاً)',
      'Reset ALL counters for this capability? (Test call history will also be wiped)',
    ))) return;
    try {
      await axios.delete(`${API}/admin/meta-review/capabilities/${capId}/calls`,
                          { headers: getAuthHeaders() });
      toast.success(t('تمت إعادة التصفير', 'Reset done'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const breadcrumb = [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التسويق', label_en: 'Marketing', path: '/dashboard?view=marketing' },
    { label_ar: 'CLOUDGM FZ LLC (تسويق)', label_en: 'CLOUDGM FZ LLC (Marketing)', path: '/marketing' },
    { label_ar: 'Meta App Review Testing', label_en: 'Meta App Review Testing', path: null },
  ];

  // Aggregate stats across all capabilities
  const stats = caps.reduce(
    (acc, c) => ({
      total_required: acc.total_required + (c.progress?.total_required || 0),
      total_current:  acc.total_current  + (c.progress?.total_current  || 0),
      completed: acc.completed + (c.status === 'completed' ? 1 : 0),
      in_progress: acc.in_progress + (c.status === 'in_progress' ? 1 : 0),
    }),
    { total_required: 0, total_current: 0, completed: 0, in_progress: 0 },
  );
  const overallPct = stats.total_required
    ? Math.round((stats.total_current / stats.total_required) * 100 * 10) / 10
    : 0;

  return (
    <div className="min-h-screen bg-[#030303] text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={() => navigate('/marketing')} />

      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-[#d4af37]" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="meta-review-title">
                    Meta App Review Testing
                  </h1>
                  <p className="text-sm text-white/50 mt-0.5">
                    {t(
                      'خطط ونفّذ وتتبع اختبارات API المطلوبة لمراجعة تطبيق Meta لكل صلاحية.',
                      'Plan, execute and track the required Meta App Review test API calls across every permission.',
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={load}
                variant="outline"
                size="sm"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="meta-review-refresh"
              >
                <RefreshCw className="h-4 w-4 me-2" />
                {t('تحديث', 'Refresh')}
              </Button>
            </div>
          </motion.div>

          {/* Token status card */}
          <TokenBar tokenPresent={tokenPresent} whoami={whoami} t={t} />

          {/* Overall progress */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
            <StatTile label={t('إجمالي الاستدعاءات', 'Total calls')}
                      value={`${stats.total_current} / ${stats.total_required}`}
                      accent="amber" />
            <StatTile label={t('نسبة الإكمال', 'Progress')}
                      value={`${overallPct}%`}
                      accent="cyan" />
            <StatTile label={t('اكتملت', 'Completed')}
                      value={`${stats.completed} / ${caps.length}`}
                      accent="emerald" />
            <StatTile label={t('قيد الاختبار', 'Testing in progress')}
                      value={`${stats.in_progress} / ${caps.length}`}
                      accent="orange" />
          </div>

          {/* Capabilities list */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {caps.map((cap) => (
                <CapabilityCard
                  key={cap.id}
                  cap={cap}
                  isAr={isAr}
                  t={t}
                  open={!!expanded[cap.id]}
                  onToggle={() => setExpanded((e) => ({ ...e, [cap.id]: !e[cap.id] }))}
                  onRunAll={() => runAll(cap.id)}
                  onRunOne={(perm, times) => runOne(cap.id, perm, times)}
                  onReset={() => resetCap(cap.id)}
                  onOpenLog={() => navigate(`/marketing/meta-app-review/${cap.id}/calls`)}
                  runAllBusy={!!busy[cap.id]}
                  permBusy={busy}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function TokenBar({ tokenPresent, whoami, t }) {
  const healthy = tokenPresent && whoami?.success;
  return (
    <div className={`rounded-2xl p-4 border flex items-center justify-between gap-3 flex-wrap ${
      healthy
        ? 'bg-emerald-500/[0.04] border-emerald-500/25'
        : 'bg-red-500/[0.04] border-red-500/30'
    }`} data-testid="meta-review-token-bar">
      <div className="flex items-center gap-3">
        <Wifi className={`h-4 w-4 ${healthy ? 'text-emerald-400' : 'text-red-400'}`} />
        <div>
          <p className="text-xs text-white/60">{t('حالة توكن Meta (WhatsApp Cloud API)', 'Meta Token status (WhatsApp Cloud API)')}</p>
          <p className={`text-sm font-semibold ${healthy ? 'text-emerald-300' : 'text-red-300'}`}>
            {healthy
              ? t(`متصل — ${whoami?.response?.name || 'مستخدم Meta'}`, `Connected — ${whoami?.response?.name || 'Meta user'}`)
              : t('غير متصل أو التوكن مفقود', 'Not connected / token missing')}
          </p>
        </div>
      </div>
      {whoami && (
        <div className="flex items-center gap-3 text-[11px] text-white/50">
          <span dir="ltr">Business: {whoami.business_id}</span>
          <span dir="ltr">WABA: {whoami.waba_id}</span>
          <span dir="ltr">Phone#: {whoami.phone_number_id}</span>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = 'amber' }) {
  const palette = {
    amber:   'from-amber-500/10 border-amber-500/25 text-amber-300',
    cyan:    'from-cyan-500/10 border-cyan-500/25 text-cyan-300',
    emerald: 'from-emerald-500/10 border-emerald-500/25 text-emerald-300',
    orange:  'from-orange-500/10 border-orange-500/25 text-orange-300',
  }[accent];
  return (
    <div className={`bg-gradient-to-br ${palette} to-white/[0.02] border rounded-xl p-3`}>
      <p className="text-[11px] text-white/50 mb-1">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  );
}

function CapabilityCard({ cap, isAr, t, open, onToggle, onRunAll, onRunOne, onReset, onOpenLog, runAllBusy, permBusy }) {
  const ic = CAP_ICON_COLOR[cap.id] || { bg: 'bg-white/5', border: 'border-white/10', text: 'text-white/70', emoji: '?' };
  const st = STATUS_STYLE[cap.status] || STATUS_STYLE.not_started;
  const pct = cap.progress?.percent || 0;

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/10 overflow-hidden" data-testid={`meta-cap-${cap.id}`}>
      {/* Header row (always visible) */}
      <div className="flex items-center justify-between gap-3 p-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center border ${ic.bg} ${ic.border} shrink-0`}>
            <span className="text-lg">{ic.emoji}</span>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm md:text-base font-semibold text-white truncate">
              {isAr ? cap.name_ar : cap.name_en}
            </h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${st.pill}`}>
                {isAr ? st.label_ar : st.label_en}
              </span>
              <span className="text-[11px] text-white/50">
                {cap.progress?.total_current} / {cap.progress?.total_required} • {pct}%
              </span>
              {cap.tier_note && (
                <span className="text-[10px] text-amber-300/80 flex items-center gap-1">
                  <Info className="h-2.5 w-2.5" /> {cap.tier_note}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            onClick={onRunAll}
            disabled={runAllBusy || cap.status === 'completed'}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold disabled:opacity-40"
            data-testid={`meta-cap-run-all-${cap.id}`}
          >
            {runAllBusy ? <Loader2 className="h-3 w-3 animate-spin me-1" /> : <Zap className="h-3 w-3 me-1" />}
            {t('تشغيل الكل', 'Run All')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onOpenLog}
            className="border-white/10 text-white/70"
            data-testid={`meta-cap-log-${cap.id}`}
          >
            <ExternalLink className="h-3 w-3 me-1" />
            {t('السجل', 'Log')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReset}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            data-testid={`meta-cap-reset-${cap.id}`}
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
          <button onClick={onToggle} className="p-1 text-white/60 hover:text-white/90"
                  data-testid={`meta-cap-toggle-${cap.id}`}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/5">
        <div className={`h-full transition-all ${pct >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
             style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>

      {/* Permissions list */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-3 border-t border-white/5 space-y-1.5">
              {(cap.permissions || []).map((p) => (
                <PermRow key={p.name} cap={cap} p={p} isAr={isAr} t={t}
                         onRun={(times) => onRunOne(p.name, times)}
                         busy={!!permBusy[`${cap.id}:${p.name}`]} />
              ))}
              {cap.external_notes && cap.external_notes.length > 0 && (
                <div className="mt-3 p-3 rounded-lg bg-blue-500/[0.04] border border-blue-500/20 text-[11px] text-blue-200 leading-relaxed">
                  <p className="font-semibold mb-1">{t('ملاحظات يدوية من Meta:', 'Manual notes from Meta:')}</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {cap.external_notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PermRow({ cap, p, isAr, t, onRun, busy }) {
  const req = p.required || 0;
  const cur = p.current  || 0;
  const done = req > 0 && cur >= req;
  const external = !!p.external;
  const pct = req > 0 ? Math.min((cur / req) * 100, 100) : 0;

  return (
    <div className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-white/[0.02]"
         data-testid={`meta-perm-${cap.id}-${p.name}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {done
          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
          : external
            ? <AlertCircle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            : <span className="h-3.5 w-3.5 rounded-full bg-white/10 shrink-0" />}
        <span className="text-[12px] text-white/85 truncate font-mono" dir="ltr">{p.name}</span>
      </div>
      <div className="text-[11px] text-white/60 min-w-[110px] text-end shrink-0" dir="ltr">
        {req === 0
          ? t('0 مكالمة اختبار', '0 API test call(s)')
          : `${cur} of ${req} required`}
      </div>
      <div className="w-24 h-1 rounded bg-white/5 overflow-hidden shrink-0">
        <div className={`h-full ${done ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
      </div>
      {external ? (
        <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/30 shrink-0">
          {t('يدوي', 'External')}
        </span>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRun(1)}
            disabled={busy || done}
            className="h-6 px-2 border-white/10 text-white/70 hover:bg-white/5 disabled:opacity-40"
            title={t('استدعاء واحد', 'Run 1 call')}
            data-testid={`meta-perm-run-${p.name}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRun(Math.max(1, Math.min(10, req - cur)))}
            disabled={busy || done || req === 0}
            className="h-6 px-2 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40 text-[10px]"
            title={t('استدعاء كل المتبقي حتى 10', 'Fire up to 10 remaining')}
            data-testid={`meta-perm-runall-${p.name}`}
          >
            {'x'}{Math.max(1, Math.min(10, req - cur))}
          </Button>
        </div>
      )}
    </div>
  );
}
